import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { getOrderInfo } from "../../../services/UserApi/OrderApi";

const POLL_INTERVAL = 5000;   // 5 seconds
const POLL_TIMEOUT  = 10 * 60 * 1000; // 10 minutes

export default function OrderPaymentWaitingPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state || {};

  const query = new URLSearchParams(location.search);

  const payment_id =
    state.payment_id ||
    query.get("payment_id") ||
    localStorage.getItem("pending_payment_id");

  const payment_url =
    state.payment_url ||
    localStorage.getItem("pending_payment_url");

  const order_id =
    state.order_id ||
    query.get("order_id") ||
    localStorage.getItem("pending_order_id");

  const returnedStatus = query.get("status");
  const returnedMessage = query.get("message");

  const [status, setStatus] = useState("waiting"); // 'waiting' | 'paid' | 'failed' | 'timeout'
  const [secondsLeft, setSecondsLeft] = useState(POLL_TIMEOUT / 1000);

  const intervalRef = useRef(null);
  const timeoutRef = useRef(null);
  const countdownRef = useRef(null);

  const clearAll = () => {
    clearInterval(intervalRef.current);
    clearTimeout(timeoutRef.current);
    clearInterval(countdownRef.current);
  };

  useEffect(() => {
    document.title = "DIDA MAX — انتظار تأكيد الدفع";

    if (payment_id) localStorage.setItem("pending_payment_id", String(payment_id));
    if (payment_url) localStorage.setItem("pending_payment_url", String(payment_url));
    if (order_id) localStorage.setItem("pending_order_id", String(order_id));

    if (returnedStatus === "failed") {
      clearAll();
      setStatus("failed");
      return;
    }

    if (returnedStatus === "success") {
      clearAll();
      setStatus("paid");
      localStorage.removeItem("pending_payment_id");
      localStorage.removeItem("pending_payment_url");
      localStorage.removeItem("pending_order_id");
      setTimeout(() => navigate("/myorders", { replace: true }), 2500);
      return;
    }

    // فتح صفحة الدفع فقط أول مرة (من state) وليس عند الرجوع من EasyKash
    if (payment_url && state.payment_url && !returnedStatus) {
      window.open(payment_url, "_blank", "noopener,noreferrer");
    }

    if (!order_id) {
      setStatus("failed");
      return;
    }

    const poll = async () => {
      try {
        const res = await getOrderInfo(order_id);
        const paymentStatus =
          res?.order?.payment_status ?? res?.payment_status ?? "";
        const normalizedStatus = String(paymentStatus).toLowerCase();

        if (normalizedStatus === "success" || normalizedStatus === "paid") {
          clearAll();
          setStatus("paid");
          localStorage.removeItem("pending_payment_id");
          localStorage.removeItem("pending_payment_url");
          localStorage.removeItem("pending_order_id");
          setTimeout(() => navigate("/myorders", { replace: true }), 2500);
        } else if (
          normalizedStatus === "failed" ||
          normalizedStatus === "canceled" ||
          normalizedStatus === "cancelled"
        ) {
          clearAll();
          setStatus("failed");
        }
      } catch {
        // retry silently
      }
    };

    intervalRef.current = setInterval(poll, POLL_INTERVAL);
    timeoutRef.current = setTimeout(() => {
      clearAll();
      setStatus("timeout");
    }, POLL_TIMEOUT);

    countdownRef.current = setInterval(() => {
      setSecondsLeft((s) => Math.max(0, s - 1));
    }, 1000);

    poll();

    return () => clearAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${String(sec).padStart(2, "0")}`;
  };

  const reopenTab = () => {
    if (payment_url) window.open(payment_url, "_blank", "noopener,noreferrer");
  };

  // ─── Waiting UI ────────────────────────────────────────────────────────────
  if (status === "waiting") {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: "background.default",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          py: 6,
        }}
      >
        <Container maxWidth="sm">
          <Paper
            elevation={0}
            sx={{
              p: { xs: 4, md: 6 },
              borderRadius: 4,
              border: "1px solid",
              borderColor: "grey.100",
              boxShadow: "0 8px 40px rgba(213,170,47,0.10)",
              textAlign: "center",
            }}
          >
            {/* Spinner */}
            <Box sx={{ position: "relative", display: "inline-flex", mb: 4 }}>
              <CircularProgress
                size={90}
                thickness={3}
                sx={{ color: "primary.main" }}
              />
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography variant="caption" fontWeight={700} color="primary.main" fontSize="1rem">
                  {formatTime(secondsLeft)}
                </Typography>
              </Box>
            </Box>

            <Typography variant="h5" fontWeight={800} mb={1}>
              في انتظار تأكيد الدفع
            </Typography>
            <Typography color="text.secondary" mb={1} fontWeight={500}>
              تم فتح صفحة الدفع في تاب جديد.
            </Typography>
            <Typography color="text.secondary" mb={4} fontSize="0.9rem">
              بعد إتمام الدفع ستُحوَّل تلقائياً لصفحة طلباتك.
            </Typography>

            {payment_id && (
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mb: 3,
                  color: "text.disabled",
                }}
              >
                رقم عملية الدفع: #{payment_id}
              </Typography>
            )}

            <Box sx={{ display: "flex", gap: 2, justifyContent: "center", flexWrap: "wrap" }}>
              <Button
                variant="contained"
                startIcon={<OpenInNewIcon />}
                onClick={reopenTab}
                sx={{
                  borderRadius: 3,
                  fontWeight: 700,
                  px: 3,
                  background: "linear-gradient(135deg, #d5aa2f 0%, #e6c76a 100%)",
                  "&:hover": {
                    background: "linear-gradient(135deg, #b08c26 0%, #d5aa2f 100%)",
                  },
                }}
              >
                فتح صفحة الدفع مجدداً
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate("/myorders")}
                sx={{ borderRadius: 3, fontWeight: 700, px: 3 }}
              >
                طلباتي
              </Button>
            </Box>
          </Paper>
        </Container>
      </Box>
    );
  }

  // ─── Paid UI ───────────────────────────────────────────────────────────────
  if (status === "paid") {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: "background.default",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Container maxWidth="sm">
          <Paper
            elevation={0}
            sx={{
              p: { xs: 4, md: 6 },
              borderRadius: 4,
              border: "2px solid",
              borderColor: "success.main",
              textAlign: "center",
            }}
          >
            <CheckCircleIcon sx={{ fontSize: 80, color: "success.main", mb: 2 }} />
            <Typography variant="h5" fontWeight={800} mb={1} color="success.dark">
              تم الدفع بنجاح! 🎉
            </Typography>
            <Typography color="text.secondary" fontWeight={500}>
              جارٍ تحويلك إلى صفحة طلباتك...
            </Typography>
          </Paper>
        </Container>
      </Box>
    );
  }

  // ─── Failed / Timeout UI ───────────────────────────────────────────────────
  const isTimeout = status === "timeout";
  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "background.default",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={0}
          sx={{
            p: { xs: 4, md: 6 },
            borderRadius: 4,
            border: "2px solid",
            borderColor: "error.light",
            textAlign: "center",
          }}
        >
          <ErrorIcon sx={{ fontSize: 80, color: "error.main", mb: 2 }} />
          <Typography variant="h5" fontWeight={800} mb={1} color="error.dark">
            {isTimeout ? "انتهت مهلة الدفع" : "فشلت عملية الدفع"}
          </Typography>
          <Typography color="text.secondary" mb={4} fontWeight={500}>
            {isTimeout
              ? "لم يتم تأكيد الدفع خلال الوقت المحدد. تحقق من طلباتك أو حاول مجدداً."
              : returnedMessage || "لم تتم عملية الدفع. يمكنك المحاولة مرة أخرى."}
          </Typography>

          <Box sx={{ display: "flex", gap: 2, justifyContent: "center", flexWrap: "wrap" }}>
            {payment_url && (
              <Button
                variant="contained"
                startIcon={<OpenInNewIcon />}
                onClick={reopenTab}
                sx={{
                  borderRadius: 3,
                  fontWeight: 700,
                  px: 3,
                  background: "linear-gradient(135deg, #d5aa2f 0%, #e6c76a 100%)",
                  "&:hover": {
                    background: "linear-gradient(135deg, #b08c26 0%, #d5aa2f 100%)",
                  },
                }}
              >
                إعادة المحاولة
              </Button>
            )}
            <Button
              variant="outlined"
              onClick={() => navigate("/myorders")}
              sx={{ borderRadius: 3, fontWeight: 700, px: 3 }}
            >
              طلباتي
            </Button>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}
